// Ported from com.codemovr.libs.commons.sdo.NumericMarker
// Subset of the most commonly used markers, grouped by category

export interface NumericMarkerEntry {
    key: string;
    token: string;
    display: string;
    category: string;
}

export const NUMERIC_MARKER_CATEGORIES = [
    'CURRENCY', 'CRYPTO', 'CURRENCY_CODE', 'PREFIX', 'SIGN',
    'SUFFIX', 'MAGNITUDE', 'ORDINAL', 'UNIT',
] as const;

export const NUMERIC_MARKERS: NumericMarkerEntry[] = [
    // CURRENCY symbols
    {key: 'DOLLAR', token: '$', display: 'Dollar ($)', category: 'CURRENCY'},
    {key: 'CENT', token: '¢', display: 'Cent (¢)', category: 'CURRENCY'},
    {key: 'POUND', token: '£', display: 'Pound (£)', category: 'CURRENCY'},
    {key: 'EURO', token: '€', display: 'Euro (€)', category: 'CURRENCY'},
    {key: 'YEN_YUAN', token: '¥', display: 'Yen / Yuan (¥)', category: 'CURRENCY'},
    {key: 'WON', token: '₩', display: 'Won (₩)', category: 'CURRENCY'},
    {key: 'RUPEE', token: '₹', display: 'Rupee (₹)', category: 'CURRENCY'},
    {key: 'RUBLE', token: '₽', display: 'Ruble (₽)', category: 'CURRENCY'},
    {key: 'LIRA', token: '₺', display: 'Lira (₺)', category: 'CURRENCY'},
    {key: 'BAHT', token: '฿', display: 'Baht (฿)', category: 'CURRENCY'},
    {key: 'FRANC', token: '₣', display: 'Franc (₣)', category: 'CURRENCY'},
    {key: 'SHEKEL', token: '₪', display: 'Shekel (₪)', category: 'CURRENCY'},
    {key: 'DONG', token: '₫', display: 'Dong (₫)', category: 'CURRENCY'},
    {key: 'PESO', token: '₱', display: 'Peso (₱)', category: 'CURRENCY'},
    {key: 'REAL', token: 'R$', display: 'Real (R$)', category: 'CURRENCY'},
    {key: 'RAND', token: 'R', display: 'Rand (R)', category: 'CURRENCY'},

    // CRYPTO
    {key: 'BITCOIN', token: '₿', display: 'Bitcoin (₿)', category: 'CRYPTO'},
    {key: 'ETHEREUM', token: 'Ξ', display: 'Ethereum (Ξ)', category: 'CRYPTO'},
    {key: 'TETHER', token: '₮', display: 'Tether (₮)', category: 'CRYPTO'},
    {key: 'USD_COIN', token: 'USDC', display: 'USD Coin (USDC)', category: 'CRYPTO'},
    {key: 'SOLANA', token: 'SOL', display: 'Solana (SOL)', category: 'CRYPTO'},
    {key: 'XRP', token: 'XRP', display: 'XRP', category: 'CRYPTO'},
    {key: 'CARDANO', token: 'ADA', display: 'Cardano (ADA)', category: 'CRYPTO'},
    {key: 'BNB', token: 'BNB', display: 'BNB', category: 'CRYPTO'},
    {key: 'DOGE', token: 'DOGE', display: 'Dogecoin (DOGE)', category: 'CRYPTO'},

    // CURRENCY CODES
    {key: 'USD_CODE', token: 'USD', display: 'US Dollar (USD)', category: 'CURRENCY_CODE'},
    {key: 'EUR_CODE', token: 'EUR', display: 'Euro (EUR)', category: 'CURRENCY_CODE'},
    {key: 'GBP_CODE', token: 'GBP', display: 'British Pound (GBP)', category: 'CURRENCY_CODE'},
    {key: 'JPY_CODE', token: 'JPY', display: 'Japanese Yen (JPY)', category: 'CURRENCY_CODE'},
    {key: 'CNY_CODE', token: 'CNY', display: 'Chinese Yuan (CNY)', category: 'CURRENCY_CODE'},
    {key: 'AUD_CODE', token: 'AUD', display: 'Australian Dollar (AUD)', category: 'CURRENCY_CODE'},
    {key: 'CAD_CODE', token: 'CAD', display: 'Canadian Dollar (CAD)', category: 'CURRENCY_CODE'},
    {key: 'CHF_CODE', token: 'CHF', display: 'Swiss Franc (CHF)', category: 'CURRENCY_CODE'},
    {key: 'INR_CODE', token: 'INR', display: 'Indian Rupee (INR)', category: 'CURRENCY_CODE'},
    {key: 'KRW_CODE', token: 'KRW', display: 'Korean Won (KRW)', category: 'CURRENCY_CODE'},
    {key: 'BRL_CODE', token: 'BRL', display: 'Brazilian Real (BRL)', category: 'CURRENCY_CODE'},
    {key: 'RUB_CODE', token: 'RUB', display: 'Russian Ruble (RUB)', category: 'CURRENCY_CODE'},

    // PREFIX operators
    {key: 'APPROX', token: '~', display: 'Approximately (~)', category: 'PREFIX'},
    {key: 'GREATER_THAN', token: '>', display: 'Greater Than (>)', category: 'PREFIX'},
    {key: 'LESS_THAN', token: '<', display: 'Less Than (<)', category: 'PREFIX'},
    {key: 'GREATER_EQUAL', token: '≥', display: 'Greater or Equal (≥)', category: 'PREFIX'},
    {key: 'LESS_EQUAL', token: '≤', display: 'Less or Equal (≤)', category: 'PREFIX'},
    {key: 'NOT_EQUAL', token: '≠', display: 'Not Equal (≠)', category: 'PREFIX'},
    {key: 'PLUS_MINUS', token: '±', display: 'Plus / Minus (±)', category: 'PREFIX'},

    // SIGNS
    {key: 'SIGN_PLUS', token: '+', display: 'Plus (+)', category: 'SIGN'},
    {key: 'SIGN_MINUS', token: '-', display: 'Minus (-)', category: 'SIGN'},

    // SUFFIX modifiers
    {key: 'PERCENT', token: '%', display: 'Percent (%)', category: 'SUFFIX'},
    {key: 'PER_MILLE', token: '‰', display: 'Per Mille (‰)', category: 'SUFFIX'},
    {key: 'BASIS_POINT', token: 'bp', display: 'Basis Point (bp)', category: 'SUFFIX'},

    // MAGNITUDE suffixes
    {key: 'THOUSAND', token: 'K', display: 'Thousand (K)', category: 'MAGNITUDE'},
    {key: 'MILLION', token: 'M', display: 'Million (M)', category: 'MAGNITUDE'},
    {key: 'BILLION', token: 'B', display: 'Billion (B)', category: 'MAGNITUDE'},
    {key: 'TRILLION', token: 'T', display: 'Trillion (T)', category: 'MAGNITUDE'},

    // ORDINALS
    {key: 'ORDINAL_ST', token: 'st', display: 'Ordinal (st)', category: 'ORDINAL'},
    {key: 'ORDINAL_ND', token: 'nd', display: 'Ordinal (nd)', category: 'ORDINAL'},
    {key: 'ORDINAL_RD', token: 'rd', display: 'Ordinal (rd)', category: 'ORDINAL'},
    {key: 'ORDINAL_TH', token: 'th', display: 'Ordinal (th)', category: 'ORDINAL'},

    // UNITS — temperature
    {key: 'DEGREE_CELSIUS', token: '°C', display: 'Celsius (°C)', category: 'UNIT'},
    {key: 'DEGREE_FAHRENHEIT', token: '°F', display: 'Fahrenheit (°F)', category: 'UNIT'},

    // UNITS — time
    {key: 'UNIT_MS', token: 'ms', display: 'Milliseconds (ms)', category: 'UNIT'},
    {key: 'UNIT_SEC', token: 's', display: 'Seconds (s)', category: 'UNIT'},
    {key: 'UNIT_MIN', token: 'min', display: 'Minutes (min)', category: 'UNIT'},
    {key: 'UNIT_HOUR', token: 'h', display: 'Hours (h)', category: 'UNIT'},
    {key: 'UNIT_DAY', token: 'd', display: 'Days (d)', category: 'UNIT'},

    // UNITS — distance
    {key: 'UNIT_MM', token: 'mm', display: 'Millimeters (mm)', category: 'UNIT'},
    {key: 'UNIT_CM', token: 'cm', display: 'Centimeters (cm)', category: 'UNIT'},
    {key: 'UNIT_M', token: 'm', display: 'Meters (m)', category: 'UNIT'},
    {key: 'UNIT_KM', token: 'km', display: 'Kilometers (km)', category: 'UNIT'},
    {key: 'UNIT_UM', token: 'µm', display: 'Micrometers (µm)', category: 'UNIT'},

    // UNITS — weight
    {key: 'UNIT_MG', token: 'mg', display: 'Milligrams (mg)', category: 'UNIT'},
    {key: 'UNIT_G', token: 'g', display: 'Grams (g)', category: 'UNIT'},
    {key: 'UNIT_KG', token: 'kg', display: 'Kilograms (kg)', category: 'UNIT'},

    // UNITS — frequency
    {key: 'UNIT_HZ', token: 'Hz', display: 'Hertz (Hz)', category: 'UNIT'},
    {key: 'UNIT_KHZ', token: 'kHz', display: 'Kilohertz (kHz)', category: 'UNIT'},
    {key: 'UNIT_MHZ', token: 'MHz', display: 'Megahertz (MHz)', category: 'UNIT'},
    {key: 'UNIT_GHZ', token: 'GHz', display: 'Gigahertz (GHz)', category: 'UNIT'},

    // UNITS — electrical
    {key: 'UNIT_V', token: 'V', display: 'Volts (V)', category: 'UNIT'},
    {key: 'UNIT_A', token: 'A', display: 'Amperes (A)', category: 'UNIT'},
    {key: 'UNIT_W', token: 'W', display: 'Watts (W)', category: 'UNIT'},
    {key: 'UNIT_KW', token: 'kW', display: 'Kilowatts (kW)', category: 'UNIT'},
    {key: 'UNIT_MW', token: 'MW', display: 'Megawatts (MW)', category: 'UNIT'},
    {key: 'UNIT_OHM', token: 'Ω', display: 'Ohms (Ω)', category: 'UNIT'},

    // OTHER
    {key: 'OR_MORE', token: '+', display: 'Or More (+)', category: 'SUFFIX'},
];
